/* ----------------------------------
 * This shit is dumb, but we need some sort of parser for the DeepDiff results.
 *
 * Accepts a DeepDiff result (object or JSON string) and extracts the set of
 * root-level keys whose values/attributes changed.
 *
 * Supported categories (DeepDiff keys):
 *  - values_changed
 *  - type_changes
 *  - dictionary_item_added
 *  - dictionary_item_removed
 *  - attribute_added
 *  - attribute_removed
 *  - iterable_item_added
 *  - iterable_item_removed
 *  - iterable_item_moved    (varies by DeepDiff version)
 *
 * The function is tolerant of unknown categories and malformed paths.
 */

type DeepDiffBucket =
  | 'values_changed'
  | 'type_changes'
  | 'dictionary_item_added'
  | 'dictionary_item_removed'
  | 'attribute_added'
  | 'attribute_removed'
  | 'iterable_item_added'
  | 'iterable_item_removed'
  | 'iterable_item_moved';

// buckets that should count toward "changed root-level keys"
const DEFAULT_CHANGE_BUCKETS: ReadonlySet<string> = new Set<DeepDiffBucket>([
  'values_changed',
  'type_changes',
  'dictionary_item_added',
  'dictionary_item_removed',
  'attribute_added',
  'attribute_removed',
  'iterable_item_added',
  'iterable_item_removed',
  'iterable_item_moved',
]);

export interface ExtractOptions {
  /**
   * Which DeepDiff buckets to consider as root-key changes.
   * Defaults to DEFAULT_CHANGE_BUCKETS above.
   */
  includeBuckets?: ReadonlySet<string>;
  /**
   * When a diff path does *not* contain a root-level key (e.g. "root[0]" if
   * the root is an array), should we skip it (true) or include a special
   * marker "__ROOT__" (false)?
   * Defaults to true (skip).
   */
  skipWhenNoRootKey?: boolean;
  /**
   * If a path can’t be parsed, ignore it (true) or throw (false).
   * Defaults to true (ignore).
   */
  ignoreUnparseablePaths?: boolean;
}

export interface ExtractResult {
  /** Unique list of changed root-level keys (order sorted ascending). */
  changedKeys: string[];
  /** Buckets we actually considered. */
  consideredBuckets: string[];
  /** Number of diff entries examined (across considered buckets). */
  examinedCount: number;
  /** Paths that failed to parse (if any and not ignored). */
  unparseablePaths: string[];
}

/**
 * Parse the first segment after "root" from a DeepDiff path like:
 *   "root['a']"                -> "a"
 *   "root['a']['b'][2]"        -> "a"
 *   "root['weird\"key']"       -> 'weird"key'
 *   "root[a]" (numeric)        -> "a" (as string if quoted; otherwise "a" raw)
 *   "root[0]"                  -> undefined (no *object* root-key segment)
 *
 * Returns undefined if there is no object key right after root
 * (e.g., root is a list) or if the path is unrecognized.
 */
export function firstRootKeyFromPath(path: string): string | undefined {
  if (typeof path !== 'string' || !path.startsWith('root')) return undefined;

  // We only care about the first [...] occurrence after "root"
  // Use a tight, non-greedy scan for bracketed segments: [ ... ]
  const bracket = /\[([^\]]+)\]/g;
  const first = bracket.exec(path);
  if (!first) return undefined;

  const raw = first[1]?.trim();
  if (!raw) return undefined;

  // If quoted (single or double), strip quotes and unescape simple cases.
  const mQuoted = raw.match(/^(['"])(.*)\1$/);
  if (mQuoted) {
    // unescape \" and \' minimally
    const inner = mQuoted[2].replace(/\\(["'])/g, '$1').replace(/\\\\/g, '\\');
    return inner;
  }

  // If not quoted, this could be a numeric index (array at root) or an identifier.
  // DeepDiff typically uses quoted keys for dicts; a bare token likely indicates array index.
  // In either case, treat unquoted numeric as array index → not a root *object* key.
  if (!Number.isNaN(Number(raw))) {
    return undefined; // root is an array; no object key here
  }

  // Fallback: return raw token (handles uncommon DeepDiff formats)
  return raw;
}

/**
 * Normalizes input into a plain object DeepDiff result.
 */
function normalizeInput(diff: unknown): Record<string, any> {
  if (typeof diff === 'string') {
    try {
      return JSON.parse(diff);
    } catch {
      throw new Error('Input looks like JSON but could not be parsed.');
    }
  }
  if (diff && typeof diff === 'object') {
    return diff as Record<string, any>;
  }
  throw new Error('DeepDiff input must be an object or a JSON string.');
}

/**
 * Extract the set of changed root-level keys from a DeepDiff result.
 */
export function extractChangedRootKeys(
  deepDiffResult: unknown,
  options: ExtractOptions = {},
): ExtractResult {
  const {
    includeBuckets = DEFAULT_CHANGE_BUCKETS,
    skipWhenNoRootKey = true,
    ignoreUnparseablePaths = true,
  } = options;

  const diffObj = normalizeInput(deepDiffResult);
  const changed = new Set<string>();
  const consideredBuckets: string[] = [];
  const unparseable: string[] = [];
  let examinedCount = 0;

  // eslint-disable-next-line no-restricted-syntax
  for (const [bucket, payload] of Object.entries(diffObj)) {
    if (!includeBuckets.has(bucket)) continue; // eslint-disable-line no-continue
    consideredBuckets.push(bucket);

    // DeepDiff’s buckets are typically dicts where keys are paths:
    //   payload = { "root['a']['b']": {old_value:..., new_value:...}, ... }
    if (!payload || typeof payload !== 'object') continue; // eslint-disable-line no-continue

    // eslint-disable-next-line no-restricted-syntax
    for (const path of Object.keys(payload)) {
      examinedCount += 1;

      try {
        const key = firstRootKeyFromPath(path);

        if (key === undefined) {
          if (!skipWhenNoRootKey) changed.add('__ROOT__');
          continue; // eslint-disable-line no-continue
        }
        changed.add(String(key));
      } catch (e) {
        if (ignoreUnparseablePaths) {
          unparseable.push(path);
          continue; // eslint-disable-line no-continue
        }
        throw new Error(
          `Failed to parse DeepDiff path "${path}": ${(e as Error).message}`,
        );
      }
    }
  }

  return {
    changedKeys: Array.from(changed).sort(),
    consideredBuckets: Array.from(new Set(consideredBuckets)),
    examinedCount,
    unparseablePaths: unparseable,
  };
}

interface ChangeEntry {
  key: string;
  type: string;
  oldValue?: any;
  newValue?: any;
}

export function extractChangedValues(diff: unknown): ChangeEntry[] {
  if (typeof diff === 'string') {
    try {
      diff = JSON.parse(diff);
    } catch {
      throw new Error('Invalid JSON string passed to extractChangedValues.');
    }
  }
  if (!diff || typeof diff !== 'object') {
    throw new Error('Expected DeepDiff result as object or JSON string.');
  }

  const results: ChangeEntry[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const [bucket, payload] of Object.entries(diff as Record<string, any>)) {
    if (!payload || typeof payload !== 'object') continue; // eslint-disable-line no-continue

    // eslint-disable-next-line no-restricted-syntax
    for (const [path, change] of Object.entries(payload)) {
      const rootKey = firstRootKeyFromPath(path);
      if (!rootKey) continue; // eslint-disable-line no-continue

      switch (bucket) {
        case 'values_changed':
        case 'type_changes':
          results.push({
            key: rootKey,
            type: bucket,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            oldValue: change.old_value,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            newValue: change.new_value,
          });
          break;

        case 'dictionary_item_added':
        case 'attribute_added':
        case 'iterable_item_added':
          results.push({
            key: rootKey,
            type: bucket,
            newValue: change,
          });
          break;

        case 'dictionary_item_removed':
        case 'attribute_removed':
        case 'iterable_item_removed':
          results.push({
            key: rootKey,
            type: bucket,
            oldValue: change,
          });
          break;

        default:
          // ignore other buckets for now, but could be added
          break;
      }
    }
  }

  return results;
}
